import { useState, Fragment } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { eur, num, dateParams, currentMonthRange, showMoney } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import DateRangeFilter from "@/components/DateRangeFilter";
import ExportMenu from "@/components/ExportMenu";
import { ReturnsTrend } from "@/components/ReturnsTrend";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loader2, ChevronDown, ChevronRight, Undo2, Euro, ShoppingBag, Truck, Percent, Wallet, Ban } from "lucide-react";

const rateClass = (r) => (r == null ? "text-muted-foreground" : r >= 15 ? "text-rose-400 font-semibold" : r >= 8 ? "text-amber-400" : "text-emerald-400");
const rateText = (r) => (r == null ? "–" : `${num(r)}%`);

function Kpi({ icon: Icon, label, value, sub, accent }) {
  return (
    <Card className="p-5" data-testid={`resi-kpi-${label}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="font-mono-erp text-[11px] uppercase tracking-widest text-muted-foreground">{label}</p>
          <p className="mt-2 font-display text-2xl font-bold tracking-tight">{value}</p>
          {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
        </div>
        <div className={`flex h-9 w-9 items-center justify-center rounded-md ${accent}`}>
          <Icon className="h-[18px] w-[18px]" />
        </div>
      </div>
    </Card>
  );
}

export default function Resi() {
  const [range, setRange] = useState(currentMonthRange);
  const [expanded, setExpanded] = useState({});
  const [sortBy, setSortBy] = useState("units");
  const toggle = (i) => setExpanded((s) => ({ ...s, [i]: !s[i] }));

  const { data, isLoading } = useQuery({
    queryKey: ["returns", range],
    queryFn: async () => (await api.get("/sales/returns", { params: dateParams(range) })).data,
  });
  const [season, setSeason] = useState(null);
  const { data: sData, isLoading: sLoading } = useQuery({
    queryKey: ["returns-products", season],
    queryFn: async () => (await api.get("/sales/returns/products", { params: season ? { season } : {} })).data,
  });
  const byProduct = sData?.by_product || [];
  const money = showMoney();

  return (
    <div>
      <PageHeader title="Resi" subtitle="Analisi dei resi: pezzi rientrati e articoli con più resi.">
        <DateRangeFilter range={range} onChange={setRange} testid="resi-date-filter" />
        <ExportMenu base="resi" testid="export-resi" />
      </PageHeader>

      {isLoading || !data ? (
        <div className="flex h-[60vh] items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
      ) : (
      <>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Kpi icon={Truck} label="Capi spediti" value={num(data.shipped_units)} sub={`${num(data.shipped_orders)} ordini spediti`} accent="bg-emerald-500/15 text-emerald-400" />
          <Kpi icon={Undo2} label="Pezzi resi" value={num(data.total_refunded_units)} sub={`${num(data.orders_with_returns)} ordini con resi`} accent="bg-rose-500/15 text-rose-400" />
          <Kpi icon={Percent} label="Tasso di reso" value={`${num(data.return_rate)}%`} sub={money ? `${num(data.return_rate_amount)}% sul valore spedito` : undefined} accent="bg-violet-500/15 text-violet-400" />
          {money && <>
          <Kpi icon={ShoppingBag} label="Valore spedito" value={eur(data.shipped_value)} sub="al netto degli sconti" accent="bg-blue-500/15 text-blue-400" />
          <Kpi icon={Euro} label="Valore reso" value={eur(data.total_refunded_amount)} sub="rimborsato alle clienti" accent="bg-amber-500/15 text-amber-400" />
          <Kpi icon={Wallet} label="Netto trattenuto" value={eur(data.net_kept)} sub="spedito meno resi" accent="bg-teal-500/15 text-teal-400" />
          </>}
        </div>
        <p className="mt-3 text-xs text-muted-foreground" data-testid="resi-criteria-note">
          Sono conteggiati come resi solo i capi rimborsati dopo la spedizione. Ordini annullati e righe annullate prima della spedizione sono esclusi e riportati sotto.
        </p>

        {data.cancellations && (
          <Card className="mt-6 p-6" data-testid="resi-cancellations-card">
            <div className="mb-4 flex items-center gap-2">
              <Ban className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-display text-lg font-semibold">Annullamenti (non sono resi)</h3>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div data-testid="resi-cancelled-orders">
                <p className="text-xs text-muted-foreground">Ordini annullati</p>
                <p className="font-display text-xl font-semibold">{num(data.cancellations.orders)} ordini · {num(data.cancellations.units)} pz</p>
                {money && <p className="text-xs text-muted-foreground">{eur(data.cancellations.amount)}</p>}
              </div>
              <div data-testid="resi-cancelled-lines">
                <p className="text-xs text-muted-foreground">Capi tolti da ordini prima della spedizione</p>
                <p className="font-display text-xl font-semibold">{num(data.cancellations.line_units)} pz</p>
                {money && <p className="text-xs text-muted-foreground">{eur(data.cancellations.line_amount)}</p>}
              </div>
            </div>
          </Card>
        )}

        {data.by_reason?.length > 0 && (
          <Card className="mt-6 p-6" data-testid="resi-reason-card">
            <h3 className="mb-1 font-display text-lg font-semibold">Motivo del reso</h3>
            <p className="mb-4 text-xs text-muted-foreground">Motivo scelto nella funzione Resi di Shopify ({num(data.reasons_from_shopify_units)} pz), altrimenti ricavato dalla nota del rimborso. Se non c'è nessuna indicazione risulta "Non specificato".</p>
            <div className="space-y-3">
              {data.by_reason.map((rsn) => {
                const max = data.by_reason[0]?.units || 1;
                return (
                  <div key={rsn.reason} data-testid={`resi-reason-${rsn.reason}`}>
                    <div className="mb-1 flex justify-between text-sm">
                      <span>{rsn.reason}</span>
                      <span className="font-semibold">{num(rsn.units)} pz{money && ` · ${eur(rsn.amount)}`}</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-secondary">
                      <div className="h-full rounded-full bg-rose-400" style={{ width: `${(rsn.units / max) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        <ReturnsTrend />

        <Card className="mt-6 overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 px-6 py-4">
            <div>
              <h3 className="font-display text-lg font-semibold">Articoli che rientrano di più</h3>
              <p className="text-xs text-muted-foreground">Su tutte le vendite dei capi della stagione, indipendentemente dal periodo selezionato.</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
            {sData && (
              <Select value={sData.season} onValueChange={setSeason}>
                <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="resi-products-season"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tutte">Tutte le stagioni</SelectItem>
                  {sData.seasons.map((s) => <SelectItem key={s} value={s}>Stagione {s}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <div className="flex gap-1 rounded-md bg-secondary p-1 text-xs">
              {[["units", "Per pezzi resi"], ["rate", "Per % di reso"]].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setSortBy(k)} data-testid={`resi-sort-${k}`}
                  className={`rounded px-3 py-1 transition-colors ${sortBy === k ? "bg-background text-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
              ))}
            </div>
            </div>
          </div>
          {sortBy === "rate" && <p className="px-6 pt-3 text-xs text-muted-foreground">Ordinati per percentuale di reso (solo articoli con almeno 10 capi spediti nella stagione).</p>}
          {sLoading ? (
            <div className="flex h-32 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : byProduct.length === 0 ? (
            <p className="px-6 py-12 text-center text-muted-foreground" data-testid="resi-empty">Nessun reso per i capi di questa stagione.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Prodotto</TableHead>
                  <TableHead className="text-right">Spediti</TableHead>
                  <TableHead className="text-right">Pezzi resi</TableHead>
                  <TableHead className="text-right">% reso</TableHead>
                  {money && <TableHead className="text-right">Valore reso</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(sortBy === "rate"
                  ? byProduct.filter((p) => p.shipped >= 10).sort((a, b) => (b.rate || 0) - (a.rate || 0))
                  : byProduct).map((p, i) => {
                  const multi = (p.colors?.length || 0) > 1;
                  const open = !!expanded[i];
                  return (
                    <Fragment key={p.name}>
                      <TableRow className={multi ? "cursor-pointer" : ""} onClick={() => multi && toggle(i)} data-testid={`resi-product-${i}`}>
                        <TableCell>{multi ? (open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />) : null}</TableCell>
                        <TableCell className="text-sm font-medium">
                          {p.name}
                          <span className="ml-2 text-xs text-muted-foreground">{multi ? `${p.colors.length} colori` : (p.colors?.[0]?.color || "")}</span>
                        </TableCell>
                        <TableCell className="text-right text-sm text-muted-foreground">{num(p.shipped)}</TableCell>
                        <TableCell className="text-right text-sm">{num(p.units)}</TableCell>
                        <TableCell className={`text-right text-sm ${rateClass(p.rate)}`} data-testid={`resi-product-${i}-rate`}>{rateText(p.rate)}</TableCell>
                        {money && <TableCell className="text-right font-semibold">{eur(p.amount)}</TableCell>}
                      </TableRow>
                      {open && multi && p.colors.map((c, j) => (
                        <TableRow key={c.color} className="bg-secondary/30 hover:bg-secondary/30" data-testid={`resi-product-${i}-color-${j}`}>
                          <TableCell></TableCell>
                          <TableCell className="pl-8 text-xs text-muted-foreground">{c.color}</TableCell>
                          <TableCell className="text-right text-xs text-muted-foreground">{num(c.shipped)}</TableCell>
                          <TableCell className="text-right text-xs text-muted-foreground">{num(c.units)}</TableCell>
                          <TableCell className={`text-right text-xs ${rateClass(c.rate)}`}>{rateText(c.rate)}</TableCell>
                          {money && <TableCell className="text-right text-xs">{eur(c.amount)}</TableCell>}
                        </TableRow>
                      ))}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Card>
      </>
      )}
    </div>
  );
}
